import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

export default function SubscriptionSuccess() {
  const { t } = useLang();
  const { refresh } = useAuth();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const tries = useRef(0);
  const fired = useRef(false);

  useEffect(() => {
    if (!sessionId) { setStatus("done"); return; }
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          // Fire the Meta subscription conversion ONLY on confirmed payment (once).
          if (!fired.current && typeof window !== "undefined" && window.fbq) {
            fired.current = true;
            window.fbq("track", "Subscribe", {
              value: data.amount || undefined,
              currency: (data.currency || "usd").toUpperCase(),
              predicted_ltv: data.amount || undefined,
            });
          }
          await refresh();
          setStatus("done");
          return;
        }
        if (["expired", "failed"].includes(data.payment_status) || tries.current > 8) {
          setStatus("done"); return;
        }
      } catch { /* keep trying */ }
      tries.current += 1;
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, refresh]);

  return (
    <div className="mx-auto flex max-w-md flex-col items-center pt-10 text-center" data-testid="subscription-success-page">
      <Logo size={72} />
      {status === "checking" ? (
        <div className="mt-8">
          <Loader2 className="mx-auto mb-3 animate-spin text-emerald-500" size={32} />
          <p className="text-sm font-medium text-gray-600">{t("success.verifying")}</p>
        </div>
      ) : (
        <>
          <CheckCircle2 className="mt-8 text-emerald-500" size={56} />
          <h1 className="mt-4 text-2xl font-extrabold text-gray-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{t("success.title")}</h1>
          <p className="mt-2 text-sm text-gray-600">{t("success.sub")}</p>
          <Link to="/evidence" data-testid="success-explore-button" className="mt-6 rounded-full bg-emerald-500 px-6 py-3 text-sm font-bold text-white hover:bg-emerald-600">{t("success.explore")}</Link>
        </>
      )}
    </div>
  );
}
